import React, { useEffect, useState } from "react";
import { Table, Tag, Button, Input, message } from "antd";
import {
  PlusOutlined,
  LeftOutlined,
  SearchOutlined,
  FilterOutlined,
  FileTextFilled,
  PieChartFilled,
  MessageFilled,
  UserOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import {
  getQuotes,
  type PaginationMeta,
  type QuoteResponse,
} from "../../utils/API";
import PageLogoutButton from "../../Components/PageLogoutButton";

interface QuoteData {
  key: string;
  date: string;
  price: string;
  quantity: number;
  options: string;
  status: string;
  comments?: string;
  subStatus: string;
}

const formatDate = (dateValue?: string) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatCurrency = (value?: number) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const mapQuoteRow = (item: QuoteResponse): QuoteData => ({
  key: String(item.id),
  date: formatDate(item.createdDate),
  price: formatCurrency(Number(item.dollars || 0)),
  quantity: Number(item.tempNum || 1),
  options: "-",
  status: item.quoteStatus || "-",
  comments: item.customerComments || "-",
  subStatus: item.subStatus || "Active",
});

const QuotesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [quotes, setQuotes] = useState<QuoteData[]>([]);
  const [overallStats, setOverallStats] = useState({
    total: 0,
    open: 0,
    expectingPo: 0,
    active: 0,
  });
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const fetchQuotes = async (
    page = pagination.page,
    limit = pagination.limit,
    search = searchQuery
  ) => {
    try {
      setLoading(true);
      const response = await getQuotes({ page, limit, search });
      setQuotes(response.data.map(mapQuoteRow));
      setPagination(response.pagination);
      setOverallStats({
        total: Number(response?.summary?.total ?? response?.pagination?.total ?? 0),
        open: Number(response?.summary?.open ?? 0),
        expectingPo: Number(response?.summary?.expectingPo ?? 0),
        active: Number(response?.summary?.active ?? 0),
      });
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to fetch quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes(1, pagination.limit, searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(searchText.trim());
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const totalQuotes = overallStats.total;
  const openQuotes = overallStats.open;
  const expectingPoQuotes = overallStats.expectingPo;
  const activeQuotes = overallStats.active;

  const columns: ColumnsType<QuoteData> = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      className: "text-slate-600",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
      render: (val) => <span className="font-bold text-slate-800">{val}</span>,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Options",
      dataIndex: "options",
      key: "options",
      className: "text-slate-500",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
      render: (status) => (
        <Tag
          color={status === "Open/WTB" ? "cyan" : "orange"}
          className="rounded px-2 border-none font-medium"
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
      render: (val) => val || "-",
    },
    {
      title: "Sub Status",
      dataIndex: "subStatus",
      key: "subStatus",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
      render: (val) => (
        <Tag
          color="blue"
          className="rounded px-4 opacity-70 border-none font-medium"
        >
          {val}
        </Tag>
      ),
    },
  ];

  return (
    <div className="p-8 bg-slate-50 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <Button
            icon={<LeftOutlined />}
            size="small"
            className="flex items-center justify-center border-gray-300"
            onClick={() => navigate("/ate-dashboard")}
          />
          <h1 className="text-2xl font-semibold text-slate-700">Quotes</h1>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="bg-[#0052cc] hover:bg-blue-700 h-10 px-6 flex items-center"
            onClick={() => navigate("/Quotes?mode=new")}
          >
            Add Quote
          </Button>
          <PageLogoutButton />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Quotes"
          value={String(totalQuotes)}
          icon={<FileTextFilled />}
          iconColor="text-blue-500"
          bgColor="bg-blue-100"
        />
        <StatCard
          title="Open/WTB"
          value={String(openQuotes)}
          icon={<PieChartFilled />}
          iconColor="text-purple-500"
          bgColor="bg-purple-100"
        />
        <StatCard
          title="Expecting Po"
          value={String(expectingPoQuotes)}
          icon={<MessageFilled />}
          iconColor="text-orange-400"
          bgColor="bg-orange-100"
        />
        <StatCard
          title="Active"
          value={String(activeQuotes)}
          icon={<UserOutlined />}
          iconColor="text-indigo-500"
          bgColor="bg-indigo-100"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6 justify-between">
        <div className="flex items-center gap-3 w-full md:w-auto ml-auto">
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined className="text-slate-400" />}
            placeholder="Search...."
            className="rounded-lg h-10 w-full md:w-64"
            suffix={
              <FilterOutlined className="text-slate-400 cursor-pointer" />
            }
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <Table
          dataSource={quotes}
          columns={columns}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              fetchQuotes(page, pageSize, searchQuery);
            },
          }}
          loading={loading}
          className="custom-antd-table"
        />
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, iconColor, bgColor }: any) => (
  <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex justify-between items-start">
    <div>
      <p className="text-slate-400 text-sm font-medium mb-1">{title}</p>
      <span className="text-3xl font-bold text-slate-800">{value}</span>
    </div>
    <div
      className={`${bgColor} ${iconColor} p-3 rounded-2xl text-xl flex items-center justify-center`}
    >
      {icon}
    </div>
  </div>
);

export default QuotesDashboard;
